import axios, { type AxiosInstance, type InternalAxiosRequestConfig } from 'axios';

import { handleUnauthorized } from '@/helpers/handleUnauthorized';
import { shouldRejectClientSideToken } from '@/helpers/jwtClient';
import { store } from '@/integrations/vuex';

const API_BASE = import.meta.env.VITE_API + '/api/internal';

const apiClient: AxiosInstance = axios.create({
  baseURL: API_BASE,
  headers: {
    'Content-Type': 'application/json',
  },
});

function resolveAuthToken (): string | null {
  if (store.state.token) {
    return store.state.token;
  }

  try {
    const saved = localStorage.getItem('token');
    if (saved === null) {
      return null;
    }

    const parsed = JSON.parse(saved) as string | null;
    if (!parsed || shouldRejectClientSideToken(parsed)) {
      return null;
    }

    return parsed;
  } catch {
    return null;
  }
}

apiClient.interceptors.request.use((config: InternalAxiosRequestConfig) => {
  const token = resolveAuthToken();

  if (token && shouldRejectClientSideToken(token)) {
    if (!config.skipUnauthorizedHandler) {
      handleUnauthorized().catch(() => {});
    }
    return Promise.reject(new Error('Session token is no longer valid'));
  }

  if (token && config.headers) {
    config.headers.Authorization = `Bearer ${token}`;
  }
  return config;
});

apiClient.interceptors.response.use(
  (response) => response,
  (error) => {
    if (
      error.response?.status === 401 &&
      !error.config?.skipUnauthorizedHandler
    ) {
      handleUnauthorized().catch(() => {});
    }
    return Promise.reject(error);
  },
);

export { apiClient };
